import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './HomePage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:7000';

function EmotionCorner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [childDetails, setChildDetails] = useState(null);
  const [formData, setFormData] = useState({
    childName: '',
    childAge: '',
    childMobileNumber: '',
    hospitalId: '',
    provisionalDiagnosis: '',
    language: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Hospital dropdown
  const [hospitals, setHospitals] = useState([]);
  const [hospitalsLoading, setHospitalsLoading] = useState(true);

  // Restore child details from navigation state
  useEffect(() => {
    if (location.state?.sessionId) {
      setChildDetails({
        sessionId: location.state.sessionId,
        childName: location.state.childName,
        childAge: location.state.childAge,
        hospitalLocation: location.state.hospitalLocation,
        provisionalDiagnosis: location.state.provisionalDiagnosis,
        language: location.state.language,
      });
    }
  }, [location.state]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    fetch(`${API_URL}/api/hospitals`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setHospitals(Array.isArray(data) ? data : []))
      .catch(() => setHospitals([]))
      .finally(() => setHospitalsLoading(false));
  }, []);

  const stages = [
    {
      id: 'stage1',
      icon: '🌟',
      iconBg: '#F0E7FF',
      title: 'Pre/Post Test',
      desc: 'Emotion recognition quiz - identify which emotion is shown in each photo',
      route: '/emotion-corner-stage-1',
      available: true,
    },
    {
      id: 'stage2',
      icon: '🎮',
      iconBg: '#DBEAFE',
      title: 'Training',
      desc: 'Emotion detection training - upload or take a photo and identify the emotion shown',
      route: '/emotion-corner-stage-2',
      available: true,
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!formData.childName.trim() || !formData.childAge.trim() || !formData.hospitalId || !formData.childMobileNumber.trim()) {
      setSubmitError('Please fill in all fields!');
      return;
    }

    if (isNaN(formData.childAge) || parseInt(formData.childAge) < 0) {
      setSubmitError('Please enter a valid age!');
      return;
    }

    if (!/^\d{10}$/.test(formData.childMobileNumber.replace(/\D/g, ''))) {
      setSubmitError('Please enter a valid 10-digit mobile number!');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          child_name: formData.childName.trim(),
          child_age: parseInt(formData.childAge),
          child_mobile_number: formData.childMobileNumber.trim(),
          hospital_id: formData.hospitalId,
          provisional_diagnosis: formData.provisionalDiagnosis.trim() || null,
          language: formData.language || null,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save session');
      }

      const session = await res.json();
      setChildDetails({
        ...formData,
        hospitalLocation: `${session.hospital_name} — ${session.hospital_location}`,
        sessionId: session.id,
        provisionalDiagnosis: formData.provisionalDiagnosis,
        language: formData.language,
      });
    } catch (err) {
      setSubmitError(err.message || 'Could not connect to server. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetForm = () => {
    setChildDetails(null);
    setSubmitError('');
    setFormData({
      childName: '',
      childAge: '',
      childMobileNumber: '',
      hospitalId: '',
      provisionalDiagnosis: '',
      language: '',
    });
  };

  const inputStyle = {
    padding: '12px 16px',
    fontSize: '15px',
    border: '1.5px solid #D1D5DB',
    borderRadius: '12px',
    fontFamily: 'Segoe UI, Inter, sans-serif',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  };
  const onFocus = (e) => { e.target.style.borderColor = '#2563EB'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; };
  const onBlur = (e) => { e.target.style.borderColor = '#D1D5DB'; e.target.style.boxShadow = 'none'; };

  return (
    <div className="home-shell">
      {/* Header ── */}
      <header className="home-header">
        <div className="home-brand">
          <span
            className="home-logo-icon"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer', fontSize: '24px' }}
            title="Back to Home"
          >
            ←
          </span>
          <span className="home-brand-name">Emotion Corner</span>
        </div>
      </header>

      {/* Show Form if no child details yet */}
      {!childDetails ? (
        <div className="home-hero">
          <div className="home-logo">👶</div>
          <h1 className="home-title">Child Details Form</h1>
          <p className="home-subtitle">
            Please fill in the child's details to begin the learning session.
          </p>

          {submitError && (
            <div style={{
              background: '#fff5f5', border: '2px solid #feb2b2', borderRadius: '12px',
              padding: '12px 16px', marginTop: '16px', fontSize: '14px', fontWeight: '600',
              color: '#c53030', display: 'flex', alignItems: 'center', gap: '8px',
              maxWidth: '500px', width: '100%',
            }}>
              <span>⚠️</span>
              <span>{submitError}</span>
            </div>
          )}

          <form
            onSubmit={handleFormSubmit}
            style={{
              background: '#fff', borderRadius: '20px', padding: '40px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.07)', maxWidth: '500px',
              width: '100%', marginTop: '30px', display: 'flex', flexDirection: 'column', gap: '20px',
            }}
          >
            {/* Child Name */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label htmlFor="childName" style={{ fontSize: '15px', fontWeight: '600', color: '#1A2340' }}>
                Child's Name *
              </label>
              <input type="text" id="childName" name="childName" value={formData.childName}
                onChange={handleInputChange} placeholder="Enter child's full name"
                style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
            </div>

            {/* Child Age */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label htmlFor="childAge" style={{ fontSize: '15px', fontWeight: '600', color: '#1A2340' }}>
                Child's Age (years) *
              </label>
              <input type="number" id="childAge" name="childAge" value={formData.childAge}
                onChange={handleInputChange} placeholder="Enter age in years" min="0" max="18"
                style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
            </div>

            {/* Child Mobile Number */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label htmlFor="childMobileNumber" style={{ fontSize: '15px', fontWeight: '600', color: '#1A2340' }}>
                Child's Mobile Number *
              </label>
              <input type="tel" id="childMobileNumber" name="childMobileNumber" value={formData.childMobileNumber}
                onChange={handleInputChange} placeholder="Enter 10-digit mobile number" maxLength="10"
                style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
            </div>

            {/* Hospital */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label htmlFor="hospitalId" style={{ fontSize: '15px', fontWeight: '600', color: '#1A2340' }}>
                🏥 Hospital Location *
              </label>
              {hospitalsLoading ? (
                <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>⏳ Loading hospitals...</p>
              ) : hospitals.length === 0 ? (
                <p style={{ fontSize: '13px', color: '#C53030', margin: 0 }}>
                  ⚠️ No hospitals registered yet. Ask your admin to add hospitals first.
                </p>
              ) : (
                <select id="hospitalId" name="hospitalId" value={formData.hospitalId}
                  onChange={handleInputChange} onFocus={onFocus} onBlur={onBlur}
                  style={{ ...inputStyle, background: '#fff', color: formData.hospitalId ? '#1A2340' : '#9CA3AF', cursor: 'pointer', appearance: 'auto' }}>
                  <option value="">— Select a hospital —</option>
                  {hospitals.map((h) => (
                    <option key={h.id} value={h.id}>{h.name} — {h.location}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Provisional Diagnosis */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label htmlFor="provisionalDiagnosis" style={{ fontSize: '15px', fontWeight: '600', color: '#1A2340' }}>
                Provisional Diagnosis <span style={{ fontWeight: '400', color: '#6B7280' }}>(optional)</span>
              </label>
              <input type="text" id="provisionalDiagnosis" name="provisionalDiagnosis"
                value={formData.provisionalDiagnosis} onChange={handleInputChange}
                placeholder="e.g. ASD, ADHD, etc."
                style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
            </div>

            {/* Language */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label htmlFor="language" style={{ fontSize: '15px', fontWeight: '600', color: '#1A2340' }}>
                Language Used to Train Child <span style={{ fontWeight: '400', color: '#6B7280' }}>(optional)</span>
              </label>
              <select id="language" name="language" value={formData.language}
                onChange={handleInputChange} onFocus={onFocus} onBlur={onBlur}
                style={{ ...inputStyle, background: '#fff', color: formData.language ? '#1A2340' : '#9CA3AF', cursor: 'pointer', appearance: 'auto' }}>
                <option value="">— Select a language —</option>
                <option value="English">English</option>
                <option value="Malayalam">Malayalam</option>
                <option value="Tamil">Tamil</option>
                <option value="Hindi">Hindi</option>
                <option value="Telugu">Telugu</option>
                <option value="Kannada">Kannada</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Submit */}
            <button type="submit" disabled={isSubmitting}
              style={{
                marginTop: '12px', padding: '13px 0', width: '100%',
                background: isSubmitting ? '#93C5FD' : '#2563EB', color: '#fff',
                border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700',
                cursor: isSubmitting ? 'not-allowed' : 'pointer', letterSpacing: '0.2px',
                transition: 'background 0.18s, transform 0.15s', fontFamily: 'Segoe UI, Inter, sans-serif',
              }}
              onMouseEnter={(e) => { if (!isSubmitting) e.target.style.background = '#1D4ED8'; }}
              onMouseLeave={(e) => { if (!isSubmitting) e.target.style.background = '#2563EB'; }}
            >
              {isSubmitting ? '⏳ Saving...' : '✓ Continue to Stages'}
            </button>
          </form>
        </div>
      ) : (
        <>
          <div className="home-hero">
            <div className="home-logo">😊</div>
            <h1 className="home-title">Emotion Corner</h1>
            <p className="home-subtitle">
              Choose a stage to continue your emotion learning journey. Learn to recognize and understand different emotions!
            </p>

            <div style={{
              background: '#DBEAFE', border: '2px solid #60A5FA', borderRadius: '12px',
              padding: '16px 24px', marginTop: '20px', fontSize: '14px', color: '#1E40AF', maxWidth: '500px',
            }}>
              <strong>Child Details:</strong> {childDetails.childName}, {childDetails.childAge} years old
              <br />
              <strong>Mobile:</strong> {childDetails.childMobileNumber}
              <br />
              <strong>Location:</strong> {childDetails.hospitalLocation}
              {childDetails.provisionalDiagnosis && (
                <><br /><strong>Diagnosis:</strong> {childDetails.provisionalDiagnosis}</>
              )}
              {childDetails.language && (
                <><br /><strong>Language:</strong> {childDetails.language}</>
              )}
              <button
                onClick={handleResetForm}
                style={{
                  marginTop: '12px', padding: '6px 12px', fontSize: '12px', fontWeight: '600',
                  color: '#1E40AF', background: '#fff', border: '1.5px solid #60A5FA',
                  borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', display: 'block',
                }}
                onMouseEnter={(e) => { e.target.style.background = '#EFF6FF'; }}
                onMouseLeave={(e) => { e.target.style.background = '#fff'; }}
              >
                Change Details
              </button>
            </div>
          </div>

          <div className="home-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', maxWidth: '700px' }}>
            {stages.map((s) => (
              <div key={s.id} className={`home-card ${!s.available ? 'home-card--dim' : ''}`}>
                <div className="home-card-icon" style={{ background: s.iconBg }}>{s.icon}</div>
                <h2 className="home-card-title">{s.title}</h2>
                <p className="home-card-desc">{s.desc}</p>
                <button
                  className="home-start-btn"
                  onClick={() => navigate(s.route, {
                    state: {
                      sessionId: childDetails.sessionId,
                      childName: childDetails.childName,
                      childAge: childDetails.childAge,
                      hospitalLocation: childDetails.hospitalLocation,
                      provisionalDiagnosis: childDetails.provisionalDiagnosis,
                      language: childDetails.language,
                    }
                  })}
                  disabled={!s.available}
                >
                  ▶ Start
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default EmotionCorner;
